/**
 * 문제 풀이 페이지
 * 채팅 인터페이스를 통한 대화형 문제 풀이
 *
 * 두 가지 모드 지원:
 * - 게임 모드: 문제를 하나씩 풀며 점수와 콤보 획득
 * - 사진 모드: 답안지 사진을 찍어 Vision API로 일괄 채점
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGameStore } from '../stores/gameStore';
import { useChatStore } from '../stores/chatStore';
import { useVisionStore } from '../stores/visionStore';
import { useAuthStore } from '../stores/authStore';
import { ChatInterface } from '../components/chat/ChatInterface';
import { ScoreDisplay } from '../components/game/ScoreDisplay';
import { ComboCounter } from '../components/game/ComboCounter';
import { HPBar } from '../components/game/HPBar';
import { StageDisplay } from '../components/game/StageDisplay';
import { PixelButton } from '../components/common/PixelButton';
import { AnswerInputSelector } from '../components/answer-input/AnswerInputSelector';
import { EquationInput } from '../components/answer-input/EquationInput';
import { EssayInput } from '../components/answer-input/EssayInput';
import { HandwritingInput } from '../components/answer-input/HandwritingInput';
import { PageSelector } from '../components/page-selection/PageSelector';
import { GradingModeSelector } from '../components/vision/GradingModeSelector';
import { PhotoCapture } from '../components/vision/PhotoCapture';
import { VisionLoadingOverlay } from '../components/vision/VisionLoadingOverlay';
import { VisionResultReview } from '../components/vision/VisionResultReview';
import { AnswerFormat } from '../types/problem';
import { AnswerValue } from '../types/answer';
import { ConfirmAnswer } from '../types/vision';
import { getPageProblems } from '../services/problemService';
import { submitAnswer, formatAnswerForSubmit } from '../services/submissionService';
import { getPageResult } from '../services/resultService';
import { gradePageWithRetry, confirmGrading } from '../services/visionService';
import { classifyError } from '../utils/visionErrorHandler';
import { soundManager } from '../utils/soundManager';
import type { VisionError } from '../types/vision';

/**
 * 페이지 모드 타입
 *
 * - page_selection: 페이지 번호 선택
 * - mode_selection: 게임/사진 모드 선택
 * - solving: 게임 모드 문제 풀이
 * - photo_flow: 사진 모드 플로우 (visionStore.mode로 세부 관리)
 * - page_complete: 페이지 완료
 */
type PageMode = 'page_selection' | 'mode_selection' | 'solving' | 'photo_flow' | 'page_complete';

/**
 * SolvePage 컴포넌트
 *
 * - 상단: 게임 HUD (점수, 콤보, HP, 스테이지)
 * - 중앙: 채팅 인터페이스
 * - 하단: 모드별 UI (페이지 선택, 모드 선택, 문제 풀이, 사진 채점 등)
 */
export const SolvePage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();

  // 게임 상태
  const { currentWorkbook, currentPage, totalPages, score, combo, hp, maxHp, isPlaying } =
    useGameStore();

  // 채팅 상태
  const { addBotMessage, addStudentMessage, clearMessages } = useChatStore();

  // 게임 상태 확장
  const { problems, currentProblemIndex, loadProblems, setCurrentProblemIndex, addScore, incrementCombo, resetCombo, setPage } = useGameStore();

  // Vision 상태
  const visionStore = useVisionStore();

  // 인증 상태 (학생 ID 필요)
  const { user } = useAuthStore();

  // 페이지 상태
  const [pageMode, setPageMode] = useState<PageMode>('page_selection');
  const [selectedPage, setSelectedPage] = useState<number | null>(null);

  // Vision 처리 중 상태
  const [isVisionProcessing, setIsVisionProcessing] = useState(false);
  const [isVisionConfirming, setIsVisionConfirming] = useState(false);

  // Phase 5: Vision API 취소를 위한 AbortController
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Phase 5: Fallback 모달 표시 상태
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [lastVisionError, setLastVisionError] = useState<VisionError | null>(null);

  // 테스트용 답형식 상태 (임시 - 향후 문제 데이터에서 가져옴)
  const [testAnswerFormat, setTestAnswerFormat] = useState<AnswerFormat>('M');

  /**
   * 페이지 진입 시 초기 메시지 표시
   */
  useEffect(() => {
    if (!name || !isPlaying) {
      navigate('/');
      return;
    }

    // 채팅 초기화
    clearMessages();
    // Vision 스토어 초기화
    visionStore.reset();

    // 환영 메시지 + 페이지 선택 요청
    const timer1 = setTimeout(() => {
      addBotMessage('안녕! 나는 채점봇이야! 🤖', 'happy');
    }, 300);

    const timer2 = setTimeout(() => {
      addBotMessage(`오늘은 "${name}" 문제집을 풀어볼까?`, 'excited');
    }, 1200);

    const timer3 = setTimeout(() => {
      addBotMessage('몇 페이지를 풀었어? 페이지 번호를 입력해줘! 📖', 'neutral');
    }, 2100);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, isPlaying]);

  /**
   * 페이지 선택 핸들러
   * → 모드 선택 화면으로 이동
   */
  const handleSelectPage = async (page: number) => {
    setSelectedPage(page);
    setPage(page); // gameStore에도 페이지 설정
    addStudentMessage(`${page}페이지!`);

    // 모드 선택 화면으로 이동
    setTimeout(() => {
      addBotMessage('어떻게 풀래? 💬 채팅으로 채점할까, 📷 사진으로 채점할까?', 'neutral');
      setPageMode('mode_selection');
    }, 500);
  };

  /**
   * 게임 모드 선택 핸들러
   * → 기존 문제 풀이 플로우
   */
  const handleSelectGameMode = async () => {
    addStudentMessage('채팅으로 채점할게요!');

    try {
      // 문제 목록 로드
      addBotMessage('문제를 불러오는 중...', 'thinking');

      const response = await getPageProblems(currentWorkbook!, selectedPage!);

      console.log('📚 문제 목록 응답:', response);
      console.log('📚 문제 개수:', response.problems.length);

      // 게임 스토어에 문제 로드
      loadProblems(response.problems, response.is_retry_mode);

      console.log('📚 loadProblems 호출 완료');

      // 문제 시작
      setTimeout(() => {
        addBotMessage(
          `${selectedPage}페이지 ${response.total_problems}개 문제! 시작하자! 🎮`,
          'excited',
          true
        );
        setPageMode('solving');

        // 첫 문제 출제 (response.problems 직접 사용)
        setTimeout(() => {
          startProblemDirect(0, response.problems);
        }, 800);
      }, 1000);
    } catch (error: any) {
      addBotMessage(
        `문제를 불러오는데 실패했어 😢: ${error.response?.data?.detail || error.message}`,
        'thinking'
      );
    }
  };

  /**
   * 사진 모드 선택 핸들러
   * → Vision 플로우 시작
   */
  const handleSelectPhotoMode = () => {
    addStudentMessage('사진으로 채점할게요!');

    // Vision 스토어 초기화 및 페이지 정보 설정
    visionStore.reset();
    visionStore.setPageInfo(currentWorkbook!, selectedPage!);
    visionStore.setMode('capturing');

    setTimeout(() => {
      addBotMessage('좋아! 답안지 사진을 찍어줘! 📸', 'excited');
      setPageMode('photo_flow');
    }, 500);
  };

  /**
   * 사진 제출 핸들러 (Vision API 호출)
   *
   * Phase 5 개선사항:
   * - gradePageWithRetry 사용 (자동 재시도)
   * - 단계별 진행 상태 표시
   * - 취소 기능 지원
   * - Fallback 안내 (게임 모드 전환)
   */
  const handlePhotoSubmit = async (image: File) => {
    // AbortController 생성 (취소용)
    const controller = new AbortController();
    setAbortController(controller);

    setIsVisionProcessing(true);
    visionStore.setMode('processing');
    visionStore.setProcessingStage('uploading');
    addBotMessage('AI가 답안을 읽고 있어... 잠깐만 기다려줘! 🔍', 'thinking');

    try {
      // Vision API 호출 (재시도 로직 포함)
      const response = await gradePageWithRetry(
        image,
        currentWorkbook!,
        selectedPage!,
        {
          // 단계 변경 콜백
          onStageChange: (stage) => {
            visionStore.setProcessingStage(stage);
          },
          // 재시도 콜백
          onRetry: ({ attempt, delay, error }) => {
            visionStore.incrementRetryCount();
            console.log(`[Vision] 재시도 ${attempt}회 (${delay}ms 후):`, error.message);
          },
          // 취소 시그널
          signal: controller.signal,
        }
      );

      console.log('📷 Vision API 응답:', response);

      // 응답 저장 (editableAnswers 자동 생성)
      visionStore.setGradeResponse(response);

      // 결과 메시지
      const unreadableMsg = response.unreadable_count > 0
        ? ` (${response.unreadable_count}개 문제는 인식 못함)`
        : '';

      addBotMessage(
        `${response.total_problems}개 문제 인식 완료!${unreadableMsg} 확인해봐! ✨`,
        'happy'
      );

    } catch (error: any) {
      console.error('Vision API 에러:', error);

      // 취소된 경우
      if (error.message === '요청이 취소되었습니다') {
        visionStore.setMode('capturing');
        addBotMessage('취소됐어! 다시 시도하거나 게임 모드로 풀어봐! 💪', 'neutral');
        return;
      }

      // 에러 분류 (이미 VisionError인 경우 그대로 사용)
      const isVisionError = error && typeof error === 'object' && 'code' in error && 'fallbackAvailable' in error;
      const visionError = isVisionError ? error : classifyError(error);
      visionStore.setLastError(visionError);

      // Fallback 가능한 에러인 경우 모달 표시
      if (visionError.fallbackAvailable) {
        setLastVisionError(visionError);
        setShowFallbackModal(true);
        addBotMessage(
          `${visionError.message} 😢 다시 시도하거나 게임 모드로 풀어볼까?`,
          'thinking'
        );
      } else {
        // Fallback 불가능한 에러 (이미지 문제 등)
        visionStore.setMode('capturing');
        addBotMessage(
          `${visionError.message} 😢 다시 찍어줘!`,
          'thinking'
        );
      }
    } finally {
      setIsVisionProcessing(false);
      visionStore.setProcessingStage(null);
      setAbortController(null);
    }
  };

  /**
   * Phase 5: Vision API 요청 취소 핸들러
   */
  const handleVisionCancel = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setIsVisionProcessing(false);
    visionStore.setProcessingStage(null);
  };

  /**
   * Phase 5: Fallback 모달에서 게임 모드로 전환
   */
  const handleFallbackToGameMode = () => {
    setShowFallbackModal(false);
    setLastVisionError(null);
    visionStore.reset();
    handleSelectGameMode();
  };

  /**
   * Phase 5: Fallback 모달에서 다시 시도
   */
  const handleFallbackRetry = () => {
    setShowFallbackModal(false);
    setLastVisionError(null);
    visionStore.setMode('capturing');
    addBotMessage('좋아! 다시 찍어봐! 📸', 'neutral');
  };

  /**
   * 사진 다시 찍기 핸들러
   */
  const handlePhotoRetake = () => {
    visionStore.setCapturedImage(null);
    visionStore.setMode('capturing');
    addBotMessage('좋아! 다시 찍어봐! 📸', 'neutral');
  };

  /**
   * 채점 확정 핸들러
   *
   * 보류된 문제가 있는 경우:
   * 1. 보류되지 않은 문제만 Vision 채점 확정
   * 2. 보류된 문제는 채팅 모드로 전환하여 개별 풀이
   */
  const handleVisionConfirm = async () => {
    if (!user?.id) {
      addBotMessage('로그인 정보를 찾을 수 없어요 😢', 'thinking');
      return;
    }

    // 보류된 문제 목록
    const deferredProblemNumbers = visionStore.deferredProblems;
    const hasDeferredProblems = deferredProblemNumbers.length > 0;

    setIsVisionConfirming(true);
    visionStore.setMode('confirming');
    addBotMessage('채점 결과를 저장하는 중... 📝', 'thinking');

    try {
      // 확정 요청 생성 (보류된 문제 제외)
      const answers: ConfirmAnswer[] = visionStore.editableAnswers
        .filter((ans) => !deferredProblemNumbers.includes(ans.problem_number))
        .map((ans) => ({
          problem_number: ans.problem_number,
          final_answer: ans.edited_answer,
        }));

      // 확정할 문제가 있으면 API 호출
      if (answers.length > 0) {
        const response = await confirmGrading({
          student_id: user.id,
          workbook_name: currentWorkbook!,
          page: selectedPage!,
          answers,
        });

        console.log('📝 채점 확정 응답:', response);

        // 저장
        visionStore.setConfirmResponse(response);

        // 결과 메시지
        const deferredMsg = hasDeferredProblems
          ? ` (${deferredProblemNumbers.length}개는 채팅으로 풀어볼게!)`
          : '';
        addBotMessage(
          `채점 완료! 정답 ${response.correct_count}개, 오답 ${response.wrong_count}개!${deferredMsg} 🎉`,
          'excited',
          true
        );

        // 스킵된 문제 안내 (재채점 권한 없음, 이미 정답 등)
        const skippedProblems = response.results.filter((r) => r.skipped);
        if (skippedProblems.length > 0) {
          const skippedMessages = skippedProblems.map((p) =>
            `${p.problem_number}번: ${p.skip_reason || '스킵됨'}`
          );
          addBotMessage(
            `⏭️ 스킵된 문제: ${skippedMessages.join(', ')}`,
            'neutral',
            true
          );
        }

        // 사운드
        if (response.correct_count > response.wrong_count) {
          soundManager.playCorrect();
        } else {
          soundManager.playWrong();
        }
      }

      // 보류된 문제가 있으면 채팅 모드로 전환
      if (hasDeferredProblems) {
        // 2초 후 채팅 모드로 전환 (서버에서 문제 목록 다시 로드)
        setTimeout(async () => {
          try {
            // 서버에서 문제 목록 다시 조회하여 실제 can_submit 상태 확인
            const freshResponse = await getPageProblems(currentWorkbook!, selectedPage!);

            // 보류된 문제 중 실제로 제출 가능한 문제만 필터링
            const submittableProblems = freshResponse.problems.filter(
              (p: { problem_number: string; can_submit: boolean }) =>
                deferredProblemNumbers.includes(p.problem_number) && p.can_submit
            );

            if (submittableProblems.length === 0) {
              // 제출 가능한 보류 문제가 없음
              addBotMessage(
                '보류한 문제들은 재채점 권한이 없어서 풀 수 없어요 😅 선생님께 재채점 허락을 받아줘!',
                'thinking'
              );

              // 결과 화면으로 이동
              const response = visionStore.confirmResponse;
              if (response) {
                setTimeout(() => {
                  navigate(`/workbooks/${currentWorkbook}/page/${selectedPage}/result`, {
                    state: {
                      result: {
                        page: response.page,
                        workbook_name: response.workbook_name,
                        attempt_number: response.attempt_number,
                        total_problems: response.total_problems,
                        correct_count: response.correct_count,
                        wrong_count: response.wrong_count,
                        pending_count: response.pending_count,
                        total_score: response.total_score,
                        problems: response.results,
                      }
                    }
                  });
                }, 2000);
              }
              return;
            }

            // 제출 가능한 보류 문제 로드
            loadProblems(submittableProblems, freshResponse.is_retry_mode);

            // Vision 스토어 초기화
            visionStore.reset();

            // 채팅 모드로 전환
            setPageMode('solving');

            // 첫 번째 보류 문제 시작
            startProblemDirect(0, submittableProblems);

            addBotMessage(
              `이제 ${submittableProblems.length}개 문제를 채팅으로 풀어보자! 🎮`,
              'happy'
            );
          } catch (error) {
            console.error('보류된 문제 로드 실패:', error);
            addBotMessage('문제를 불러오는데 실패했어요 😢', 'thinking');
            visionStore.reset();
            setPageMode('mode_selection');
          }
        }, 2000);
      } else {
        // 보류된 문제 없으면 결과 화면으로 이동
        setTimeout(() => {
          const response = visionStore.confirmResponse;
          if (response) {
            navigate(`/workbooks/${currentWorkbook}/page/${selectedPage}/result`, {
              state: {
                result: {
                  page: response.page,
                  workbook_name: response.workbook_name,
                  attempt_number: response.attempt_number,
                  total_problems: response.total_problems,
                  correct_count: response.correct_count,
                  wrong_count: response.wrong_count,
                  pending_count: response.pending_count,
                  total_score: response.total_score,
                  problems: response.results,
                }
              }
            });
          }
        }, 2000);
      }

    } catch (error: any) {
      console.error('채점 확정 에러:', error);
      visionStore.setError(error.response?.data?.detail || error.message);
      visionStore.setMode('reviewing');
      addBotMessage(
        `저장에 실패했어 😢 다시 시도해줘! (${error.response?.data?.detail || '네트워크 오류'})`,
        'thinking'
      );
    } finally {
      setIsVisionConfirming(false);
    }
  };

  /**
   * 사진 모드 취소 (모드 선택으로 돌아가기)
   */
  const handlePhotoCancelToModeSelection = () => {
    visionStore.reset();
    setPageMode('mode_selection');
    addBotMessage('어떻게 풀래? 💬 채팅으로 채점할까, 📷 사진으로 채점할까?', 'neutral');
  };

  /**
   * 문제 시작 (문제 배열 직접 전달)
   */
  const startProblemDirect = (index: number, problemList: any[]) => {
    console.log('🎯 startProblem 호출:', index);
    console.log('🎯 전체 문제 수:', problemList.length);

    const problem = problemList[index];

    if (!problem) {
      console.log('❌ 문제 없음 - 페이지 완료 처리');
      handlePageComplete();
      return;
    }

    console.log('🎯 현재 문제:', problem);

    if (!problem.can_submit) {
      // 이미 채점 완료 → SKIP
      addBotMessage(
        `문제 ${problem.problem_number}번은 이미 채점 완료! SKIP! ⏭️`,
        'happy',
        true
      );
      setTimeout(() => startProblemDirect(index + 1, problemList), 1000);
      return;
    }

    // 문제 출제
    setCurrentProblemIndex(index);
    setTestAnswerFormat(problem.answer_format as AnswerFormat);

    // 참고 메시지가 있으면 먼저 표시
    if (problem.reference) {
      addBotMessage(
        `💡 참고: ${problem.reference}`,
        'neutral',
        true
      );
    }

    addBotMessage(
      `문제 ${problem.problem_number}번!`,
      'neutral',
      true
    );
  };

  /**
   * 문제 시작 (gameStore에서 참조)
   */
  const startProblem = (index: number) => {
    startProblemDirect(index, problems);
  };

  /**
   * 답안 제출 핸들러 (실제 API 연동)
   */
  const handleAnswerSubmit = async (answer: AnswerValue) => {
    const problem = problems[currentProblemIndex];
    if (!problem) return;

    try {
      // 학생 메시지로 답안 표시
      const answerText = formatAnswerForSubmit(answer);
      addStudentMessage(answerText);

      // 채점 API 호출
      const result = await submitAnswer({
        workbook_name: currentWorkbook!,
        page: selectedPage!,
        problem_number: problem.problem_number,
        attempt_number: problem.next_attempt_number!,
        user_answer: answerText,
        answer_image_url: null,
      });

      // 결과 피드백
      if (result.is_correct) {
        addBotMessage(result.message, 'happy');
        addScore(result.score);
        incrementCombo();
        soundManager.playCorrect();
      } else {
        addBotMessage(result.message, 'encouraging');
        resetCombo();
        soundManager.playWrong();
      }

      // 다음 문제
      setTimeout(() => {
        if (result.page_completed) {
          handlePageComplete();
        } else {
          startProblem(currentProblemIndex + 1);
        }
      }, 1500);
    } catch (error: any) {
      addBotMessage('제출 실패 😢 다시 시도해줘!', 'thinking');
    }
  };

  /**
   * 페이지 완료 처리
   * 결과 화면으로 이동
   */
  const handlePageComplete = async () => {
    // selectedPage 또는 currentPage 사용 (fallback)
    const pageNum = selectedPage || currentPage;

    if (!pageNum || !currentWorkbook) {
      console.error('페이지 정보가 없습니다 - 홈으로 이동');
      addBotMessage('페이지 정보를 찾을 수 없어요 😅', 'thinking');
      setTimeout(() => navigate('/'), 1500);
      return;
    }

    addBotMessage(`${pageNum}페이지 완료! 🎉`, 'excited', true);

    try {
      // 결과 조회 후 결과 화면으로 이동
      const result = await getPageResult(currentWorkbook, pageNum);

      setTimeout(() => {
        navigate(`/workbooks/${currentWorkbook}/page/${pageNum}/result`, {
          state: { result }
        });
      }, 1500);
    } catch (error: any) {
      // 404 에러 (문제가 없거나 제출 없음) - 정상 케이스로 처리
      if (error.response?.status === 404) {
        // 제출이 없어도 페이지 완료 UI 표시
        setPageMode('page_complete');
        setTimeout(() => {
          addBotMessage(
            '다음 페이지 할래? 아니면 다른 페이지?',
            'neutral'
          );
        }, 1000);
        return; // 에러 로그 없이 종료
      }

      // 기타 에러
      console.error('결과 조회 실패:', error);
      setPageMode('page_complete');
      setTimeout(() => {
        addBotMessage(
          '다음 페이지 할래? 아니면 다른 페이지?',
          'neutral'
        );
      }, 1000);
    }
  };

  /**
   * 다음 페이지 핸들러
   */
  const handleNextPage = () => {
    const nextPage = selectedPage! + 1;
    setPageMode('page_selection');
    setSelectedPage(null);
    visionStore.reset();
    handleSelectPage(nextPage);
  };

  /**
   * 다른 페이지 선택 핸들러
   */
  const handleSelectOtherPage = () => {
    setPageMode('page_selection');
    setSelectedPage(null);
    visionStore.reset();
    addBotMessage('좋아! 다른 페이지를 선택해줘!', 'neutral');
  };

  /**
   * 문제 넘어가기 핸들러
   * 답안을 제출하지 않고 다음 문제로 이동
   */
  const handleSkipProblem = () => {
    const problem = problems[currentProblemIndex];
    if (!problem) return;

    // 건너뛰기 메시지
    addStudentMessage('이 문제는 나중에 풀게요!');

    setTimeout(() => {
      addBotMessage(
        `문제 ${problem.problem_number}번 건너뛰기! 다음 문제로 가자!`,
        'neutral',
        true
      );

      // 콤보 리셋 (건너뛰기는 페널티)
      resetCombo();

      // 다음 문제로 이동
      setTimeout(() => {
        const nextIndex = currentProblemIndex + 1;
        if (nextIndex >= problems.length) {
          // 마지막 문제를 건너뛴 경우 페이지 완료
          handlePageComplete();
        } else {
          startProblem(nextIndex);
        }
      }, 800);
    }, 500);
  };

  /**
   * 나가기 핸들러
   * 문제 풀이 중이면 결과 확인 제안
   */
  const handleExit = async () => {
    // Vision 스토어 정리
    visionStore.reset();

    // 페이지 선택 또는 모드 선택 모드면 바로 나가기
    if (pageMode === 'page_selection' || pageMode === 'mode_selection') {
      if (window.confirm('문제 풀이를 종료하시겠습니까?')) {
        navigate('/');
      }
      return;
    }

    // 사진 모드면 바로 나가기
    if (pageMode === 'photo_flow') {
      if (window.confirm('사진 채점을 종료하시겠습니까?')) {
        navigate('/');
      }
      return;
    }

    // 문제 풀이 중이면 결과 확인 제안
    if (selectedPage && pageMode === 'solving') {
      const confirmResult = window.confirm('채점 결과를 확인하시겠어요?');

      if (confirmResult) {
        try {
          const result = await getPageResult(currentWorkbook!, selectedPage);
          navigate(`/workbooks/${currentWorkbook}/page/${selectedPage}/result`, {
            state: { result }
          });
        } catch (error) {
          console.error('결과 조회 실패:', error);
          navigate('/');
        }
      } else {
        navigate('/');
      }
    } else {
      // 페이지 완료 모드면 바로 나가기
      if (window.confirm('문제 풀이를 종료하시겠습니까?')) {
        navigate('/');
      }
    }
  };

  return (
    <div className="h-screen bg-black flex flex-col overflow-hidden">
      {/* Vision 로딩 오버레이 (Phase 5: 단계별 진행, 취소 기능) */}
      <VisionLoadingOverlay
        isVisible={isVisionProcessing}
        stage={visionStore.processingStage}
        retryCount={visionStore.retryCount}
        onCancel={handleVisionCancel}
      />

      {/* Phase 5: Vision 실패 시 Fallback 모달 */}
      {showFallbackModal && lastVisionError && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-gray-900 border-4 border-[#9bbc0f] rounded-lg p-6 max-w-sm w-full">
            {/* 에러 아이콘 */}
            <div className="text-center mb-4">
              <span className="text-5xl">{lastVisionError.code === 'PAGE_NOT_FOUND' ? '📋' : '😢'}</span>
            </div>

            {/* 에러 메시지 */}
            <h3 className="font-pixel text-sm text-[#9bbc0f] text-center mb-2">
              {lastVisionError.code === 'PAGE_NOT_FOUND' ? '문제가 없는 페이지' : '사진 인식 실패'}
            </h3>
            <p className="font-pixel text-xs text-gray-400 text-center mb-6">
              {lastVisionError.message}
            </p>

            {/* 선택 버튼 */}
            <div className="space-y-3">
              {lastVisionError.code === 'PAGE_NOT_FOUND' ? (
                /* PAGE_NOT_FOUND: 다른 페이지 선택 */
                <button
                  onClick={() => {
                    setShowFallbackModal(false);
                    setLastVisionError(null);
                    handleSelectOtherPage();
                  }}
                  className="w-full font-pixel text-sm py-3 px-4
                             bg-[#9bbc0f] text-black rounded-lg
                             hover:bg-[#8dad0e] transition-colors
                             border-b-4 border-[#6b8a0a]
                             active:border-b-0 active:mt-1"
                >
                  📖 다른 페이지 선택
                </button>
              ) : (
                /* 일반 에러: 다시 찍기 */
                <button
                  onClick={handleFallbackRetry}
                  className="w-full font-pixel text-sm py-3 px-4
                             bg-[#9bbc0f] text-black rounded-lg
                             hover:bg-[#8dad0e] transition-colors
                             border-b-4 border-[#6b8a0a]
                             active:border-b-0 active:mt-1"
                >
                  📷 다시 찍기
                </button>
              )}
              <button
                onClick={handleFallbackToGameMode}
                className="w-full font-pixel text-sm py-3 px-4
                           bg-gray-700 text-white rounded-lg
                           hover:bg-gray-600 transition-colors
                           border-b-4 border-gray-800
                           active:border-b-0 active:mt-1"
              >
                💬 채팅 모드로 풀기
              </button>
              <button
                onClick={() => {
                  setShowFallbackModal(false);
                  setLastVisionError(null);
                  handlePhotoCancelToModeSelection();
                }}
                className="w-full font-pixel text-xs py-2 px-4
                           text-gray-500 hover:text-gray-400 transition-colors"
              >
                ← 모드 선택으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto w-full flex flex-col h-full p-4">
        {/* 문제집 이름 (최상단) */}
        <div className="mb-2 flex-shrink-0">
          <p className="font-pixel text-xs text-center text-[#9bbc0f]">
            📚 {currentWorkbook}
          </p>
        </div>

        {/* 게임 HUD (고정) - 컴팩트 버전 - 모바일에서 숨김, 게임 모드에서만 표시 */}
        {pageMode === 'solving' && (
          <>
            <div className="hidden md:grid grid-cols-3 gap-2 mb-3 flex-shrink-0">
              {/* 점수 */}
              <div>
                <ScoreDisplay score={score} animated />
              </div>

              {/* 콤보 */}
              <div>
                <ComboCounter combo={combo} />
              </div>

              {/* HP 바 */}
              <div>
                <HPBar current={hp} max={maxHp} />
              </div>
            </div>

            {/* 스테이지 표시 (별도 행) - 모바일에서 숨김 */}
            <div className="hidden md:block mb-2 flex-shrink-0">
              <StageDisplay
                page={currentPage}
                totalPages={totalPages}
              />
            </div>
          </>
        )}

        {/* 채팅 인터페이스 (스크롤 가능) */}
        <div className="flex-1 mb-4 min-h-0">
          <ChatInterface />
        </div>

        {/* 모드별 UI 표시 */}
        <div className="mb-4 bg-gray-900 p-4 rounded-lg border-2 border-gray-700 flex-shrink-0">
          {/* 페이지 선택 모드 */}
          {pageMode === 'page_selection' && (
            <PageSelector
              totalPages={totalPages}
              onSelectPage={handleSelectPage}
            />
          )}

          {/* 모드 선택 (게임/사진) */}
          {pageMode === 'mode_selection' && (
            <GradingModeSelector
              onSelectGameMode={handleSelectGameMode}
              onSelectPhotoMode={handleSelectPhotoMode}
            />
          )}

          {/* 문제 풀이 모드 (게임) */}
          {pageMode === 'solving' && (
            <>
              <h3 className="font-pixel text-sm text-[#9bbc0f] mb-3">
                📝 답안 입력
              </h3>
              {testAnswerFormat === 'E' || testAnswerFormat === 'F' || testAnswerFormat === 'I' ? (
                <EquationInput
                  key={currentProblemIndex}
                  onSubmit={handleAnswerSubmit}
                  onOcrResult={(latex) => {
                    addBotMessage('이 수식이 맞아?', 'neutral', false, latex);
                  }}
                />
              ) : testAnswerFormat === 'H' ? (
                <HandwritingInput
                  key={currentProblemIndex}
                  onSubmit={handleAnswerSubmit}
                />
              ) : testAnswerFormat === 'T' ? (
                <EssayInput
                  key={currentProblemIndex}
                  onSubmit={handleAnswerSubmit}
                  workbookName={currentWorkbook!}
                  page={selectedPage!}
                  problemNumber={problems[currentProblemIndex]?.problem_number || ''}
                />
              ) : (
                <AnswerInputSelector
                  key={currentProblemIndex}
                  answerFormat={testAnswerFormat}
                  onSubmit={handleAnswerSubmit}
                />
              )}
            </>
          )}

          {/* 사진 모드 플로우 */}
          {pageMode === 'photo_flow' && (
            <>
              {/* 사진 촬영/선택 */}
              {(visionStore.mode === 'capturing' || visionStore.mode === 'previewing') && (
                <PhotoCapture
                  onSubmit={handlePhotoSubmit}
                  onCancel={handlePhotoCancelToModeSelection}
                  isLoading={isVisionProcessing}
                />
              )}

              {/* 인식 결과 확인/수정 */}
              {(visionStore.mode === 'reviewing' || visionStore.mode === 'confirming') && (
                <VisionResultReview
                  answers={visionStore.editableAnswers}
                  overallConfidence={visionStore.gradeResponse?.overall_confidence || 0}
                  onUpdateAnswer={visionStore.updateAnswer}
                  onConfirm={handleVisionConfirm}
                  onRetake={handlePhotoRetake}
                  isConfirming={isVisionConfirming}
                  deferredProblems={visionStore.deferredProblems}
                  onDeferProblem={visionStore.deferProblem}
                  onUndeferProblem={visionStore.undeferProblem}
                />
              )}
            </>
          )}

          {/* 페이지 완료 모드 */}
          {pageMode === 'page_complete' && (
            <div className="space-y-3">
              <p className="font-pixel text-sm text-center text-[#9bbc0f] mb-4">
                페이지 완료! 다음은?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <PixelButton
                  variant="primary"
                  size="medium"
                  onClick={handleNextPage}
                >
                  다음 페이지
                </PixelButton>
                <PixelButton
                  variant="secondary"
                  size="medium"
                  onClick={handleSelectOtherPage}
                >
                  다른 페이지
                </PixelButton>
              </div>
            </div>
          )}
        </div>

        {/* 하단 액션 버튼 (고정) */}
        <div className="flex justify-center items-center gap-3 flex-shrink-0">
          {/* 나가기 버튼 (항상 표시) */}
          <PixelButton variant="secondary" size="small" onClick={handleExit}>
            ← 나가기
          </PixelButton>

          {/* 넘어가기 버튼 (게임 모드 문제 풀이 중에만 표시) */}
          {pageMode === 'solving' && (
            <PixelButton variant="primary" size="small" onClick={handleSkipProblem}>
              넘어가기 →
            </PixelButton>
          )}
        </div>
      </div>
    </div>
  );
};
